import { type FormEvent, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { action } from "mobx";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Button, Box, TextField, Typography } from "@mui/material";
import { routes } from "@/router/Routes";
import { interactionStore, authStore } from "@/stores/Stores";
import { Page } from "@/components/Page";
import { useReferrer } from "@/hooks/useReferrer";
import { BackLink } from "@/components/BackLink";
import SecurityCodeDialog from "./SecurityCode";

export const Login = observer(() => {
  const { t } = useTranslation();
  const referrer = useReferrer([routes.pages.login]);

  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const [emailTouched, setEmailTouched] = useState(false);
  const [showSecurityCodeDialog, setShowSecurityCodeDialog] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setShowSecurityCodeDialog(authStore.otpSession !== null && authStore.otpSession !== "");
  }, [authStore.otpSession]);

  // Password managers can fill the inputs without firing change events, and Chrome hides
  // autofilled values from JS until the user interacts with the page, so read the inputs on submit.
  const syncFromInputs = action(() => {
    authStore.unknownUser.email = emailInputRef.current?.value ?? "";
    authStore.unknownUser.password = passwordInputRef.current?.value ?? "";
  });

  const onSubmitHandler = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    syncFromInputs();
    setEmailTouched(true);

    if (!authStore.unknownUser.canLogin || interactionStore.loading) {
      return;
    }

    try {
      await authStore.login();
    } catch (error) {
      console.error("Login failed", error);
    }
  };

  const showEmailError = emailTouched && !authStore.unknownUser.isEmailValid;

  return (
    <>
      {referrer && <BackLink url={referrer} sx={{ marginLeft: "1rem" }} />}
      <Page
        title="Sign In"
        subtitle={
          <Typography variant="body2">
            {t("Need an account?")} <Link to={routes.pages.signup}>{t("Register here")}</Link>
          </Typography>
        }
        showLanguages
      >
        <form onSubmit={onSubmitHandler} noValidate>
          <TextField
            inputRef={emailInputRef}
            onChange={action((event) => {
              authStore.unknownUser.email = event.target.value;
            })}
            onBlur={() => setEmailTouched(true)}
            fullWidth
            name="email"
            type="email"
            label={t("Email address")}
            variant="outlined"
            size="medium"
            error={showEmailError}
            helperText={showEmailError ? t("Invalid email address") : ""}
            autoComplete="username"
          />

          <TextField
            inputRef={passwordInputRef}
            onChange={action((event) => {
              authStore.unknownUser.password = event.target.value;
            })}
            fullWidth
            name="password"
            type="password"
            label={t("Password")}
            variant="outlined"
            size="medium"
            autoComplete="current-password"
          />

          <Button
            disabled={interactionStore.loading}
            sx={{ marginTop: "1rem" }}
            fullWidth
            type="submit"
            variant="contained"
            color="primary"
          >
            {t("Log in")}
          </Button>
        </form>

        <Box sx={{ marginTop: "1rem" }}>
          <Typography variant="body2" color="primary">
            <Link to={routes.pages.resetPassword}>{t("Forgot password?")}</Link>
          </Typography>
        </Box>
      </Page>
      {referrer && <Box sx={{ height: "1rem" }} />}

      <SecurityCodeDialog
        open={showSecurityCodeDialog}
        onConfirm={() => {
          authStore.login(authStore.securityCode.join(""), authStore.otpSession);
        }}
      />
    </>
  );
});
